import { z } from 'zod';
import { offerRequestEstimatedVolumeSchema } from '../../offer-request-management/projectSize.js';
import { partnerMatchingScoreSchema } from './partnerMatchingScore.js';
// Where a partner stands on one lead, derived from the match status and the customer-facing engagement.
// Ordered by how far the lead has progressed, so the enum order doubles as a ranking.
export const leadStatusSchema = z.enum([
    'InPoolNotYetShown',
    'VisibleInCockpit',
    'Expired',
    // The waterfall run closed before reaching this partner, so the lead can no longer be handed to them.
    'NeverShown',
    'DeclinedByPartner',
    'PartnerAcceptedAwaitingClient',
    'PartnerAcceptedClientDeclined',
    'ClientAndPartnerAccepted',
]);
// Both pool states mean the lead was never handed over: one is still waiting its turn, the other ran out.
export const isDistributedLeadStatus = (leadStatus) => leadStatus !== 'InPoolNotYetShown' && leadStatus !== 'NeverShown';
export const suitablePartnerDrivingDistanceSchema = z.object({
    distanceInMeters: z.number(),
    // Google routes some pairs without returning a duration.
    durationInSeconds: z.number().nullable(),
});
// Which team manages the partner, and so which revenue brand the lead is billed under.
export const partnerTeamSchema = z.enum(['Houzy', 'Devis']);
// What the partner has left to work the lead with, read from their current products and wallet rather than
// from the matching run.
export const suitablePartnerContractSchema = z.discriminatedUnion('type', [
    // The end of the commitment period the partner was in, which is the next point they can walk away — the same
    // date the near-contract-end factor scores against. Null only where a running subscription has no commitment
    // window at all; a partner holding no subscription gets no contract rather than an empty one.
    z.object({ type: z.literal('Subscription'), endsAt: z.iso.datetime().nullable() }),
    z.object({ type: z.literal('Credit'), creditsRemaining: z.number() }),
    z.object({ type: z.literal('Lead'), leadsRemaining: z.number().int() }),
    // Time-based old contracts have no capacity to count down.
    z.object({ type: z.literal('Time') }),
]);
export const suitablePartnerSchema = z.object({
    partnerUuid: z.uuid(),
    partnerId: z.number().int(),
    partnerName: z.string(),
    leadStatus: leadStatusSchema,
    drivingDistance: suitablePartnerDrivingDistanceSchema.nullable(),
    // The run the partner belongs to: the one that handed them the lead, or for a partner not reached yet the run
    // ranking them — in a preview, the run confirming it would create.
    matchingRunNumber: z.number().int(),
    // Old-contract matches get a billable OfferRequestPartnerAssignment and join on this to reuse its
    // opening status and billing label. Subscription matches have no assignment.
    offerRequestPartnerAssignmentId: z.number().int().nullable(),
    // Carried per row rather than fetched per partner, which a table would make one request each. Null only in
    // a matching preview whose scoring ran out of time; a persisted run always scored every partner it lists.
    score: partnerMatchingScoreSchema.nullable(),
    team: partnerTeamSchema.nullable(),
    contract: suitablePartnerContractSchema.nullable(),
    // When an offer request last went out to the partner, by email or into their cockpit.
    lastReceivedOfferOn: z.iso.datetime().nullable(),
    // Null where the partner has no weekly maximum to count against.
    leadsSentInCurrentWeek: z.number().int().nullable(),
});
// How a stream's waterfall slots (max. 4 per offer request) are taken right now, so reviewing a rematch shows how many
// of the partners it ranks the waterfall will actually reach.
export const suitablePartnerSlotsSchema = z.object({
    // The partner accepted the lead.
    locked: z.number().int(),
    // The partner has the lead and not decided yet.
    pending: z.number().int(),
    free: z.number().int(),
});
export const suitablePartnerStreamSchema = z.object({
    // Partners that survived the hard filters.
    suitableCount: z.number().int(),
    // Grouped by run, newest run first, each in the order the waterfall hands the lead out, including the
    // partners it never reached.
    partners: z.array(suitablePartnerSchema),
    // Only for a rematch preview: before the first run every slot is free, and the persisted view shows who holds them.
    slots: suitablePartnerSlotsSchema.nullable(),
});
// The work categories a selection covers and the answers given per category, keyed by work-category key.
// Same two fields the platform already passes selections around by, so a consumer renders this with whatever
// it already renders an offer request's selection with.
export const workCategoriesSelectionSchema = z.object({
    keys: z.array(z.string()),
    answers: z.record(z.string(), z.record(z.string(), z.unknown())),
});
// A locality the run's candidate pool covered. One ZIP can split across several, and the run bounds itself
// by all of them, so the lead's single address ZIP does not describe the pool on its own.
export const matchingRunPostalCodeSchema = z.object({
    code: z.string(),
    localityName: z.string(),
});
// The lead side of a finished run as the engine saw it, read from the run's own snapshot. The offer request
// keeps moving underneath it — the validation team corrects work categories after a run and the address can
// be edited — so anything explaining a run reads this instead.
export const matchingRunLeadSnapshotSchema = z.object({
    requiredNumberOfCredits: z.number().int(),
    estimatedVolume: offerRequestEstimatedVolumeSchema.nullable(),
    postalCodes: z.array(matchingRunPostalCodeSchema),
    workCategoriesSelection: workCategoriesSelectionSchema,
});
export const offerRequestSuitablePartnersResponseSchema = z.object({
    // Separates "matching found nobody" from "matching has not run". False for a preview too: it scores the
    // pool without persisting a run, even when it previews a rematch next to the partners matched so far.
    hasMatchingRun: z.boolean(),
    runAt: z.iso.datetime().nullable(),
    // The run whose ranking the partners not reached yet are in: the latest run, or in a preview the run confirming
    // it would create. Partners of lower runs were matched by an earlier run. Null only when no run exists and
    // nothing is previewed.
    rankedRunNumber: z.number().int().nullable(),
    // Null when no run exists and nothing is previewed; a preview snapshots the lead it is about to match on.
    leadSnapshot: matchingRunLeadSnapshotSchema.nullable(),
    oldContract: suitablePartnerStreamSchema,
    subscription: suitablePartnerStreamSchema,
});
